import { AlertCircle, RefreshCw, WifiOff, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ApiError, errorMessage } from '@/lib/api/errors';
import { cn } from '@/lib/cn';

/** Carregando: esqueleto no formato do conteúdo (linhas de lista por padrão). */
export function LoadingState({
  rows = 4,
  label = 'Carregando…',
  className,
}: {
  rows?: number;
  label?: string;
  className?: string;
}) {
  return (
    <div role="status" aria-live="polite" aria-label={label} className={cn('flex flex-col gap-3', className)}>
      <span className="sr-only">{label}</span>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="size-9 shrink-0 rounded-full" />
          <div className="flex flex-1 flex-col gap-2">
            <Skeleton className="h-3.5" style={{ width: `${70 - i * 9}%` }} />
            <Skeleton className="h-3 w-1/3" />
          </div>
        </div>
      ))}
    </div>
  );
}

type EmptyStateProps = {
  icon: LucideIcon;
  title: string;
  /** O que é esta área e o que fazer para preenchê-la: estado vazio ensina a interface. */
  description: ReactNode;
  action?: ReactNode;
  className?: string;
};

export function EmptyState({ icon: Icon, title, description, action, className }: EmptyStateProps) {
  return (
    <div className={cn('flex flex-col items-center px-6 py-12 text-center', className)}>
      <span className="mb-4 inline-flex size-12 items-center justify-center rounded-full bg-brand-soft text-brand-strong dark:text-brand">
        <Icon className="size-6" aria-hidden />
      </span>
      <h3 className="text-base font-bold">{title}</h3>
      <div className="mt-1.5 max-w-md text-sm text-muted-foreground">{description}</div>
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  );
}

type ErrorStateProps = {
  error: unknown;
  title?: string;
  onRetry?: () => void;
  retrying?: boolean;
  className?: string;
};

/** Erro ao carregar: mensagem do servidor, ação de tentar de novo e o código para o suporte. */
export function ErrorState({ error, title, onRetry, retrying, className }: ErrorStateProps) {
  const network = error instanceof ApiError && error.isNetworkError;
  const Icon = network ? WifiOff : AlertCircle;
  const requestId = error instanceof ApiError ? error.requestId : undefined;
  return (
    <div role="alert" className={cn('flex flex-col items-center px-6 py-12 text-center', className)}>
      <span className="mb-4 inline-flex size-12 items-center justify-center rounded-full bg-status-red-bg text-status-red-fg">
        <Icon className="size-6" aria-hidden />
      </span>
      <h3 className="text-base font-bold">
        {title ?? (network ? 'Sem conexão com o servidor' : 'Não foi possível carregar')}
      </h3>
      <p className="mt-1.5 max-w-md text-sm text-muted-foreground">{errorMessage(error)}</p>
      {requestId ? (
        <p className="mt-2 text-xs text-muted-foreground">
          Código para o suporte: <code className="font-mono">{requestId}</code>
        </p>
      ) : null}
      {onRetry ? (
        <Button variant="outline" className="mt-5" onClick={onRetry} loading={retrying}>
          {retrying ? null : <RefreshCw aria-hidden />}
          Tentar novamente
        </Button>
      ) : null}
    </div>
  );
}
